import { useEffect, useId, useMemo, useState } from "react";
import {
  chessArrow,
  type ChessArrow as Arrow,
  type ChessSquare as Square,
} from "@/shared/components/chessboard";
import { Box, IconButton, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { applySanMove, type EvaluationScore } from "@chessla/shared";
import { CapturedPieces } from "@/features/board/review/CapturedPieces";
import { BoardFrame } from "@/shared/components/chessboard/BoardFrame";
import type { Evaluation } from "@/shared/components/evaluation";
import { continuationEvaluationForWhite } from "./continuation-evaluation";
import { useBoardPreferences } from "@/features/board";
import { ChesslaBoardSurface } from "@/features/board/surface";

interface ContinuationStep {
  fen: string;
  san: string;
  from: string;
  to: string;
}

/**
 * Replays a list of SAN moves from a starting FEN,
 * producing a FEN + from/to squares for each step.
 */
function replayContinuation(startingFen: string, sanMoves: string[]): ContinuationStep[] {
  const steps: ContinuationStep[] = [];
  let fen = startingFen;
  for (const san of sanMoves) {
    const move = applySanMove(fen, san);
    if (!move) break;
    steps.push({ fen: move.fen, san: move.san, from: move.uci.slice(0, 2), to: move.uci.slice(2, 4) });
    fen = move.fen;
  }
  return steps;
}

interface BestMoveNavigatorProps {
  startingFen: string;
  bestMoveSan: string;
  bestFrom: string;
  bestTo: string;
  continuation: string[];
  continuationEvals: number[] | null;
  continuationScores: EvaluationScore[] | null;
  orientation: "white" | "black";
  boardWidth: number | string;
  evaluation: Evaluation | null;
}

/**
 * A chessboard with ◀ ▶ navigation showing Stockfish's best continuation.
 * Starts at the position before the best move, then steps through the PV line.
 */
export const BestMoveNavigator = ({
  startingFen,
  bestMoveSan,
  bestFrom,
  bestTo,
  continuation,
  continuationEvals,
  continuationScores,
  orientation,
  boardWidth,
  evaluation: initialEvaluation,
}: BestMoveNavigatorProps) => {
  const { settings } = useBoardPreferences("nikola");
  const [stepIndex, setStepIndex] = useState(0); // 0 = first move (best move)
  const [markerFocus, setMarkerFocus] = useState<{ fen: string; square: Square } | null>(null);

  // Build the full sequence: best move + continuation
  const allMoves = useMemo(() => [bestMoveSan, ...continuation], [bestMoveSan, continuation]);
  const steps = useMemo(() => replayContinuation(startingFen, allMoves), [startingFen, allMoves]);

  // Dynamic eval: use pre-computed evals if available, otherwise fall back to initial
  const evaluation: Evaluation | null = continuationEvaluationForWhite(
    startingFen,
    stepIndex,
    continuationScores,
    continuationEvals,
    initialEvaluation,
  );

  // Display logic: show the position BEFORE the current step's move,
  // with an arrow showing WHERE that move goes.
  // Step 0 = starting position + arrow for best move
  // Step 1 = position after best move + arrow for continuation[0]
  // etc.
  const currentStep = steps[stepIndex] ?? null;
  // Position to show: the FEN from the PREVIOUS step (or startingFen for step 0)
  const displayFen = stepIndex === 0 ? startingFen : (steps[stepIndex - 1]?.fen ?? startingFen);
  useEffect(() => setMarkerFocus(null), [displayFen]);
  // Arrow: shows the current step's move (what's about to happen)
  const displayArrow: Arrow = currentStep
    ? chessArrow(currentStep.from as Square, currentStep.to as Square, "rgb(34,197,94)")
    : chessArrow(bestFrom as Square, bestTo as Square, "rgb(34,197,94)");

  const totalSteps = steps.length;
  const moveLabel = currentStep ? `${stepIndex + 1}/${totalSteps} ${currentStep.san}` : bestMoveSan;

  const instanceId = useId();

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <BoardFrame width={boardWidth} evaluation={settings.showEvaluation ? { evaluation, orientation } : undefined}>
        <Box sx={{ width: "100%", border: "1px solid", borderColor: "divider" }}>
          <ChesslaBoardSurface
            settings={settings}
            relationshipFocus={markerFocus}
            id={`compare-best-nav-${instanceId}`}
            position={displayFen}
            orientation={orientation}
            width="100%"
            animationDuration={0}
            allowDragging={false}
            arrows={settings.showBestMoveArrow ? [displayArrow] : []}
            onSquareClick={(square) =>
              setMarkerFocus((current) =>
                current?.fen === displayFen && current.square === square ? null : { fen: displayFen, square },
              )
            }
          />
        </Box>
      </BoardFrame>
      <CapturedPieces fen={displayFen} />
      {totalSteps > 1 && (
        <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
          <IconButton
            size="small"
            onClick={() => setStepIndex(0)}
            disabled={stepIndex <= 0}
            sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
          >
            <SkipPreviousIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography
            variant="caption"
            sx={{ minWidth: 90, textAlign: "center", fontWeight: 600, color: "#22c55e", fontSize: "0.7rem" }}
          >
            {moveLabel}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setStepIndex((i) => Math.min(totalSteps - 1, i + 1))}
            disabled={stepIndex >= totalSteps - 1}
            sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
          >
            <NavigateNextIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setStepIndex(totalSteps - 1)}
            disabled={stepIndex >= totalSteps - 1}
            sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
          >
            <SkipNextIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
